/**
 * TagDeploymentPanel — Shows the live deployment URL, copy actions,
 * QR code, and NFC programming guidance for an assigned tag.
 * 
 * Used on Room Tags, Restroom Tags, and Retailer Alert Tags.
 * Supports EN/FR bilingual labels.
 */
import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Copy,
  Check,
  ExternalLink,
  QrCode,
  Smartphone,
  Download,
  Link2,
} from 'lucide-react';

const DEPLOY_DOMAIN = 'https://hotelsontap.com';

const i18n = {
  en: {
    deploymentUrl: 'Deployment URL',
    liveUrl: 'Live URL',
    copyUrl: 'Copy URL',
    copied: 'Copied!',
    openLiveSurvey: 'Open Live Survey',
    showQr: 'QR Code',
    nfcGuidance: 'Program your NFC tag with this URL. This is the production endpoint guests open when tapping.',
    qrTitle: 'QR Code — Deployment',
    downloadQr: 'Download QR',
    nfcReady: 'NFC Ready',
  },
  fr: {
    deploymentUrl: 'URL de déploiement',
    liveUrl: 'URL en direct',
    copyUrl: 'Copier l\'URL',
    copied: 'Copié!',
    openLiveSurvey: 'Ouvrir le sondage',
    showQr: 'Code QR',
    nfcGuidance: 'Programmez votre étiquette NFC avec cette URL. C\'est le lien de production que les clients ouvriront.',
    qrTitle: 'Code QR — Déploiement',
    downloadQr: 'Télécharger QR',
    nfcReady: 'NFC Prêt',
  },
};

function getDeployUrl(tagId) {
  return `${DEPLOY_DOMAIN}/api/tap?card=${tagId}`;
}

export default function TagDeploymentPanel({ tagId, tagLabel, color = 'green', lang = 'en' }) {
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const t = i18n[lang] || i18n.en;
  const url = getDeployUrl(tagId);

  const colorMap = {
    green:   { bg: 'bg-green-500/8',   border: 'border-green-500/25', text: 'text-green-400',   accent: 'bg-green-600 hover:bg-green-700', hoverBg: 'hover:bg-green-500/15', qrFg: '#4ade80' },
    blue:    { bg: 'bg-blue-500/8',     border: 'border-blue-500/25',  text: 'text-blue-400',    accent: 'bg-blue-600 hover:bg-blue-700',   hoverBg: 'hover:bg-blue-500/15',  qrFg: '#60a5fa' },
    fuchsia: { bg: 'bg-fuchsia-500/8',  border: 'border-fuchsia-500/25', text: 'text-fuchsia-400', accent: 'bg-fuchsia-600 hover:bg-fuchsia-700', hoverBg: 'hover:bg-fuchsia-500/15', qrFg: '#e879f9' },
  };
  const c = colorMap[color] || colorMap.green;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById(`qr-deploy-${tagId}`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);
      const link = document.createElement('a');
      link.download = `${tagId}-qr.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <>
      {/* Deployment URL Panel */}
      <div className={`mt-2 p-3 rounded-lg ${c.bg} border ${c.border} space-y-2.5`} data-testid={`deploy-panel-${tagId}`}>
        {/* Header row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1.5">
            <Link2 className={`w-3.5 h-3.5 ${c.text}`} />
            <span className={`text-[11px] font-semibold uppercase tracking-wider ${c.text}`}>{t.deploymentUrl}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Smartphone className={`w-3 h-3 ${c.text} opacity-60`} />
            <span className={`text-[10px] ${c.text} opacity-60`}>{t.nfcReady}</span>
          </div>
        </div>

        {/* URL display */}
        <div className="flex items-center space-x-2 p-2 rounded bg-[hsl(217,19%,10%)] border border-[hsl(217,19%,22%)]">
          <code className="text-[11px] text-white/90 font-mono truncate flex-1 select-all" data-testid={`deploy-url-${tagId}`}>
            {url}
          </code>
          <button
            onClick={handleCopy}
            className={`flex-shrink-0 p-1.5 rounded ${c.hoverBg} transition-colors`}
            title={t.copyUrl}
            data-testid={`copy-deploy-url-${tagId}`}
          >
            {copied ? (
              <Check className={`w-3.5 h-3.5 ${c.text}`} />
            ) : (
              <Copy className={`w-3.5 h-3.5 ${c.text}`} />
            )}
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex items-center space-x-1.5">
          <Button
            size="sm"
            onClick={handleCopy}
            className={`flex-1 h-7 text-xs ${c.accent} text-white`}
            data-testid={`copy-btn-${tagId}`}
          >
            {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
            {copied ? t.copied : t.copyUrl}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.open(url, '_blank')}
            className={`flex-1 h-7 text-xs border-[hsl(217,19%,27%)] ${c.text} ${c.hoverBg}`}
            data-testid={`open-live-${tagId}`}
          >
            <ExternalLink className="w-3 h-3 mr-1" />
            {t.openLiveSurvey}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setQrOpen(true)}
            className={`h-7 text-xs border-[hsl(217,19%,27%)] ${c.text} ${c.hoverBg} px-2`}
            data-testid={`qr-btn-${tagId}`}
          >
            <QrCode className="w-3.5 h-3.5" />
          </Button>
        </div>

        {/* NFC Guidance */}
        <p className="text-[10px] text-[hsl(215,20%,50%)] leading-relaxed">
          {t.nfcGuidance}
        </p>

        {/* Inline QR thumbnail */}
        <div className="flex items-center space-x-3 pt-1">
          <button
            onClick={() => setQrOpen(true)}
            className={`p-1.5 rounded border border-[hsl(217,19%,25%)] bg-white cursor-pointer hover:opacity-90 transition-opacity`}
            data-testid={`qr-thumb-${tagId}`}
          >
            <QRCodeSVG value={url} size={48} level="M" bgColor="#ffffff" fgColor="#000000" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-[hsl(215,20%,55%)] truncate font-mono">{tagId}</p>
            {tagLabel && <p className="text-[10px] text-[hsl(215,20%,45%)] truncate">{tagLabel}</p>}
          </div>
        </div>
      </div>

      {/* QR Modal */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className={`flex items-center space-x-2 ${c.text}`}>
              <QrCode className="w-5 h-5" />
              <span>{t.qrTitle}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center space-y-4 py-4">
            {/* Large QR */}
            <div className="bg-white p-4 rounded-lg">
              <QRCodeSVG
                id={`qr-deploy-${tagId}`}
                value={url}
                size={240}
                level="H"
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </div>

            {/* Tag info */}
            <div className="text-center space-y-1">
              <p className="text-sm font-mono text-white font-semibold">{tagId}</p>
              {tagLabel && <p className="text-xs text-[hsl(215,20%,55%)]">{tagLabel}</p>}
            </div>

            {/* URL */}
            <div className="w-full p-2.5 rounded bg-[hsl(217,19%,8%)] border border-[hsl(217,19%,22%)]">
              <code className="text-[11px] text-white/80 font-mono break-all select-all">{url}</code>
            </div>

            {/* Modal actions */}
            <div className="flex items-center space-x-2 w-full">
              <Button
                size="sm"
                onClick={handleCopy}
                className={`flex-1 ${c.accent} text-white`}
                data-testid={`modal-copy-${tagId}`}
              >
                {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
                {copied ? t.copied : t.copyUrl}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.open(url, '_blank')}
                className={`flex-1 border-[hsl(217,19%,27%)] ${c.text}`}
                data-testid={`modal-open-${tagId}`}
              >
                <ExternalLink className="w-4 h-4 mr-1.5" />
                {t.openLiveSurvey}
              </Button>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownloadQR}
              className="w-full border-[hsl(217,19%,27%)] text-[hsl(215,20%,75%)]"
              data-testid={`modal-download-qr-${tagId}`}
            >
              <Download className="w-4 h-4 mr-1.5" />
              {t.downloadQr}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
